import { AnvilProvider } from '@servicetitan/anvil2';
import { StrictMode, FC, useEffect, PropsWithChildren } from 'react';
import { provide, Provider, useDependencies, ProviderProps } from '@servicetitan/react-ioc';
import { observer } from 'mobx-react';
import { AppStore } from './stores/app.store';
import { AuthApi, createApiClient, createApiConfig } from '@servicetitan/task-hub-api';
import { getTaskHubEnvironmentBaseUrl, getTaskHubEnvironment } from 'shared';
import { AppRouter } from './app-router';
import { BrowserRouter } from 'react-router-dom';
import { Loader } from './common/components/loader';

import './app.css';

export const AppProvider: FC<PropsWithChildren> = provide({
    singletons: [AppStore],
})(({ children }) => {
    const apiConfig = createApiConfig({
        basePath: getTaskHubEnvironmentBaseUrl(getTaskHubEnvironment()) + '/api',
    });

    const singletons: ProviderProps['singletons'] = [
        {
            provide: AuthApi,
            useValue: createApiClient(AuthApi, apiConfig),
        },
    ];

    return <Provider singletons={singletons}>{children}</Provider>;
});

export const AppUnwrapped: FC = observer(() => {
    const [{ initApp, loading, initialized }] = useDependencies(AppStore);

    useEffect(() => {
        initApp();
    }, [initApp]);

    const showLoading = !initialized || loading;
    if (showLoading) {
        return <Loader fullPage />;
    }

    return <AppRouter />;
});

export const App: FC = () => {
    return (
        <BrowserRouter>
            <StrictMode>
                <AnvilProvider>
                    <AppProvider>
                        <AppUnwrapped />
                    </AppProvider>
                </AnvilProvider>
            </StrictMode>
        </BrowserRouter>
    );
};
